// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from './context/theme-context'
import { AppLoader } from './components/AppLoader'
import { PageTransition } from './components/PageTransition'
import { VerifyScreen } from './pages/VerifyScreen'
import { WelcomeScreen } from './pages/WelcomeScreen'
import { LinkGoogleScreen } from './pages/LinkGoogleScreen'
import { ConsentScreen } from './pages/ConsentScreen'
import { ChatScreen } from './pages/ChatScreen'
import { PrepNoteReviewScreen } from './pages/PrepNoteReviewScreen'
import { PhoneAuthScreen } from './pages/PhoneAuthScreen'
import { LinkExpiredScreen } from './pages/LinkExpiredScreen'
import { LinkInvalidatedScreen } from './pages/LinkInvalidatedScreen'
import { KioskWelcomeScreen } from './pages/KioskWelcomeScreen'
import { KioskConsentScreen } from './pages/KioskConsentScreen'
import { KioskHandbackScreen } from './pages/KioskHandbackScreen'
import { EmergencyScreen } from './pages/EmergencyScreen'
import { captureDeeplinkTokenFromUrl } from './lib/session'
import { RequireSession, RequireVerifyEntry, RequireFirebase } from './components/RouteGuards'

function App() {
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Grab the self-eval deeplink token (?token=) before any client-side navigation drops it.
    captureDeeplinkTokenFromUrl()
    const t = setTimeout(() => setLoading(false), 900)
    return () => clearTimeout(t)
  }, [])

  if (loading) return <AppLoader />

  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          {/* Onboarding order: verify → link-google (+bind) → phone → consent. Phone follows
              bind because it needs a bound session (backend 409s identity_not_bound). */}
          <Route path="/verify"           element={<RequireVerifyEntry><PageTransition><VerifyScreen /></PageTransition></RequireVerifyEntry>} />
          <Route path="/link-google"      element={<RequireSession><RequireFirebase><PageTransition><LinkGoogleScreen /></PageTransition></RequireFirebase></RequireSession>} />
          {/* /phone is endpoint-driven (no Firebase) — RequireSession is enough. */}
          <Route path="/phone"            element={<RequireSession><PageTransition><PhoneAuthScreen /></PageTransition></RequireSession>} />
          <Route path="/"                 element={<PageTransition><WelcomeScreen /></PageTransition>} />
          <Route path="/consent"          element={<RequireSession><PageTransition><ConsentScreen /></PageTransition></RequireSession>} />
          <Route path="/chat"             element={<RequireSession><PageTransition><ChatScreen /></PageTransition></RequireSession>} />
          <Route path="/visit-summary"    element={<RequireSession><PageTransition><PrepNoteReviewScreen /></PageTransition></RequireSession>} />
          <Route path="/prep-note"        element={<RequireSession><PageTransition><PrepNoteReviewScreen /></PageTransition></RequireSession>} />
          <Route path="/link-expired"       element={<PageTransition><LinkExpiredScreen /></PageTransition>} />
          <Route path="/link-invalidated"   element={<PageTransition><LinkInvalidatedScreen /></PageTransition>} />
          {/* Emergency hard-stop — no PageTransition; full-screen takeover */}
          <Route path="/emergency"          element={<EmergencyScreen />} />
          {/* Kiosk flow — no PageTransition, these are full-screen takeover screens */}
          <Route path="/kiosk"         element={<KioskWelcomeScreen />} />
          <Route path="/kiosk/consent" element={<KioskConsentScreen />} />
          <Route path="/kiosk/done"    element={<KioskHandbackScreen />} />
          <Route path="*"                 element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  )
}

export default App
